import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  saveTeamOrder,
  createStripeConfirmationQuote,
  finalizeStripePayment,
  createPaymentFeeQuote,
} from "actions/teamActions";
import { isEmpty, noop, filter, head, get } from "lodash/fp";
import { Checkbox, CircularProgress } from "@material-ui/core";
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from "@material-ui/core";
import { isNotEmpty } from "../../utils/funcUtils";
import { getSelectedTeam } from "../../reducers/teamSelectors";
import { format, utcToZonedTime } from "date-fns-tz";
import { asHKCurrency, formatCurrency } from "../helpers";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import StripeCheckoutForm from "./StripeCheckoutForm";
import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";
import Select from "../common/Select";
import { getMootRoundTimeOptions } from "../../utils/dateUtils";
import moot from "../settings/Moot";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_CLIENT_SECRET);

const PaymentProcessing = ({
  existingTeamData = {},
  onPaymentTypeChanged = noop,
  onPaymentCompleted = noop,
  selectedTeam = {},
  saveTeamOrder = noop,
  getSelectedTeam = noop,
  paymentOverride = false,
  showExsitingPaymentNotification = true,
  paymentLabel = "Please select the payment amount.",
  minAmount = 1500,
  maxAmount = 10000,
  initialAmount = 1500,
  balanceOwed = 0,
  paymentHelperText = `Payment remitted must be no less than ${asHKCurrency(minAmount)}`,
  transactionType,
  showBankTransferRegInstructions = true,
  onPaymentInProcess = noop,
  createStripeConfirmationQuote = noop,
  finalizeStripePayment = noop,
  createPaymentFeeQuote = noop,
}) => {
  const labelStyle = {
    fontWeight: "bold",
  };

  const lineStyle = {
    margin: ".5rem 0",
  };

  const [isBusy, setBusy] = useState(false);
  const [paymentType, setPaymentType] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState(initialAmount);
  const [finalPaymentAmount, setFinalPaymentAmount] = useState(initialAmount);
  const [paypalCardCountry, setPaypalCardCountry] = useState("INTL");
  const [paypalFeeQuote, setPaypalFeeQuote] = useState();
  const [payProcessingFees, setPayProcessingFees] = useState(true);
  const [paymentError, setPaymentError] = useState(false);

  const applyProcessingFeeChoice = (feeQuote) =>
    payProcessingFees
      ? {
          ...feeQuote,
          processingFeesPaid: true,
        }
      : {
          ...feeQuote,
          feeAmount: 0,
          grossAmount: finalPaymentAmount,
          feePolicy: "NO_FEE",
          rate: 0,
          fixedFee: 0,
          processingFeesPaid: false,
        };

  const getPaypalFeeQuote = async () => {
    const feeQuote = await createPaymentFeeQuote({
      processor: "paypal",
      paymentMethod: "paypal-card",
      country: paypalCardCountry,
      amount: finalPaymentAmount,
      teamId: selectedTeam.id,
      transactionType,
    });
    const adjustedFeeQuote = applyProcessingFeeChoice(feeQuote);

    setPaypalFeeQuote(adjustedFeeQuote);
    return adjustedFeeQuote;
  };

  useEffect(() => {
    const fetchPaypalFeeQuote = async () => {
      if (
        paymentType !== "paypal" ||
        paymentError ||
        !selectedTeam?.id ||
        !transactionType
      ) {
        setPaypalFeeQuote(null);
        return;
      }

      await getPaypalFeeQuote();
    };

    fetchPaypalFeeQuote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    createPaymentFeeQuote,
    finalPaymentAmount,
    payProcessingFees,
    paymentError,
    paymentType,
    paypalCardCountry,
    selectedTeam?.id,
    transactionType,
  ]);

  const validatePaymentAmount = (data, actions) => {
    if (paymentError) {
      return actions.reject();
    }

    if (
      (paymentAmount < minAmount || paymentAmount > maxAmount) &&
      paymentOverride === false
    ) {
      setPaymentError(
        `Please enter a payment amount between ${asHKCurrency(minAmount)} and ${asHKCurrency(maxAmount)}`,
      );
      return actions.reject();
    } else {
      setPaymentError(false);
      return actions.resolve();
    }
  };

  const createOrder = async (data, actions, paymentMethod) => {
    const paymentFeeQuote = paypalFeeQuote || (await getPaypalFeeQuote());
    const orderObject = {
      purchase_units: [
        {
          amount: {
            value: paymentFeeQuote?.grossAmount,
          },
          description: selectedTeam.school,
          custom_id: `${selectedTeam.id}|${transactionType}|${paymentFeeQuote?.baseAmount}|${paymentFeeQuote?.feeAmount}|${paymentFeeQuote?.feePolicy}|${paymentFeeQuote?.rate ?? ""}|${paymentFeeQuote?.fixedFee ?? ""}|${paymentFeeQuote?.country || ""}|${paymentMethod}`,
        },
      ],
      payment_source: {
        paypal: {
          experience_context: {
            shipping_preference: "NO_SHIPPING",
          },
        },
      },
    };
    return await actions.order.create(orderObject);
  };
  const onApprove = async (data, actions) => {
    const order = await actions.order.get(data.orderID);
    let details = order;
    if (order.status !== "APPROVED") {
      await actions.restart();
    } else {
      try {
        details = await actions.order.capture();
      } catch (e) {
        await actions.order.restart();
      }
      try {
        setBusy(true);
        onPaymentInProcess();

        const amount = get("purchase_units[0].amount.value", details);
        const description = `PayPal: ${get("purchase_units[0].description", details)}`;
        const customIdParts = get(
          "purchase_units[0].custom_id",
          details,
        )?.split("|");
        const paymentFeeQuote =
          customIdParts?.length >= 9
            ? {
                processor: "paypal",
                baseAmount: customIdParts[2],
                feeAmount: customIdParts[3],
                grossAmount: amount,
                feePolicy: customIdParts[4],
                rate: customIdParts[5],
                fixedFee: customIdParts[6],
                country: customIdParts[7],
                paymentMethod: customIdParts[8],
                processingFeesPaid: parseFloat(customIdParts[3]) > 0,
              }
            : null;

        if (paymentFeeQuote) {
          details.paymentFeeQuote = paymentFeeQuote;
        }

        await saveTeamOrder(
          existingTeamData.id,
          details,
          description,
          amount,
          transactionType,
        );
        await onPaymentCompleted();
        await getSelectedTeam(true);
      } catch {}

      setBusy(false);
    }
    return order;
  };
  const onProcessStripePayment = async (
    stripePaymentIntent,
    paymentFeeQuote,
    options = {},
  ) => {
    try {
      setBusy(true);
      onPaymentInProcess();

      const amount = stripePaymentIntent.amount
        ? stripePaymentIntent.amount / 100
        : paymentFeeQuote.grossAmount;
      const description = `Stripe: ${selectedTeam.school}`;

      const stripePaymentData = {
        id: stripePaymentIntent.id,
        amount: stripePaymentIntent.amount,
        currency: stripePaymentIntent.currency,
        status: stripePaymentIntent.status,
        created: stripePaymentIntent.created,
        metadata: stripePaymentIntent.metadata,
        payment_processor: "stripe",
        paymentFeeQuote,
      };

      await saveTeamOrder(
        existingTeamData.id,
        stripePaymentData,
        description,
        amount,
        transactionType,
      );
      if (options.complete !== false) {
        await onPaymentCompleted();
        await getSelectedTeam(true);
      }
    } catch {}

    setBusy(false);
  };

  const onStripePaymentPending = async () => {
    await getSelectedTeam(true);

    if (transactionType !== "INITIAL") {
      await onPaymentCompleted();
    }
  };

  const onPaymentAmountEntered = async (e) => {
    const finalAmount = parseFloat(e.target.value).toFixed(2);
    setPaymentAmount(finalAmount);
    setFinalPaymentAmount(finalAmount);
    if (
      (finalAmount < minAmount || finalAmount > maxAmount) &&
      paymentOverride === false
    ) {
      setPaymentError(
        `Please enter a payment amount between ${asHKCurrency(minAmount)} and ${asHKCurrency(maxAmount)}`,
      );
    } else {
      setPaymentError(false);
    }
  };
  const initialPayment = head(
    filter((p) => p.type !== "FEE", selectedTeam?.Payments),
  );

  const buildPaymentAmounts = () => {
    const depositAmount = parseFloat(moot.teamDepositAmount ?? 1500);
    const smallTeamRegistrationFee = parseFloat(
      moot.inPersonRegistrationFeeSmallTeam ?? 9500,
    );
    const registrationFee = parseFloat(moot.inPersonRegistrationFee ?? 10000);

    let firstItem = {
      name: `${asHKCurrency(depositAmount)} - Non refundable deposit`,
      id: depositAmount.toFixed(2),
    };

    if (balanceOwed < 0) {
      firstItem = {
        name: `${asHKCurrency(Math.abs(balanceOwed))} - Remaining balance`,
        id: Math.abs(balanceOwed).toFixed(2),
      };
    }
    return [
      firstItem,
      {
        name: `${asHKCurrency(smallTeamRegistrationFee)} - Small team registration fee`,
        id: smallTeamRegistrationFee.toFixed(2),
      },
      {
        name: `${asHKCurrency(registrationFee)} - Registration fee`,
        id: registrationFee.toFixed(2),
      },
    ];
  };
  return (
    <>
      {isNotEmpty(initialPayment) && showExsitingPaymentNotification && (
        <Typography>
          A payment in the amount of {asHKCurrency(initialPayment.amount)}{" "}
          received on{" "}
          {format(
            utcToZonedTime(initialPayment.createdAt, "Asia/Hong_Kong"),
            "eee. d MMMM h:mm aa",
          )}{" "}
          HKT
        </Typography>
      )}
      {(isEmpty(initialPayment) || !showExsitingPaymentNotification) && (
        <>
          {isBusy ? <CircularProgress size={35} /> : null}
          {!isBusy && (
            <>
              <FormControl component="fieldset">
                <FormLabel component="legend" required>
                  Please select your intended payment method. Please note, bank
                  transfers must be remitted separate from the Team Account
                  directly through the bank.
                </FormLabel>
                <RadioGroup
                  name="type"
                  value={paymentType}
                  onChange={(e) => {
                    setPaymentType(e.target.value);
                    onPaymentTypeChanged(e.target.value);
                  }}
                  row
                  style={{ marginTop: "1rem" }}
                >
                  <FormControlLabel
                    value="paypal"
                    control={<Radio color="primary" required />}
                    label="PayPal / Credit Card"
                  />
                  <FormControlLabel
                    value="stripe"
                    control={<Radio color="primary" required />}
                    label="Stripe / Credit Card / Link / Alipay"
                  />
                  <FormControlLabel
                    value="bankTransfer"
                    control={<Radio color="primary" required />}
                    label="Bank Transfer"
                  />
                </RadioGroup>
              </FormControl>
              {paymentType !== "bankTransfer" && (
                <>
                  <Typography
                    variant={transactionType === "INITIAL" ? "body1" : "h5"}
                    style={{ marginBottom: "1rem", marginTop: "1rem" }}
                  >
                    {paymentLabel}
                  </Typography>
                  <Grid item xs={12} md={6} style={{ marginBottom: "1rem" }}>
                    <Select
                      onChange={(e) => {
                        onPaymentAmountEntered(e);
                        setPaypalFeeQuote(null);
                      }}
                      label="Amount"
                      value={paymentAmount}
                      options={buildPaymentAmounts()}
                      required
                      helperText={paymentHelperText}
                    />

                    {paymentError && (
                      <Typography color="error">{paymentError}</Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={6} style={{ marginBottom: "1rem" }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          checked={payProcessingFees}
                          onChange={(e) => {
                            setPayProcessingFees(e.target.checked);
                            setPaypalFeeQuote(null);
                          }}
                        />
                      }
                      label="I'd like to pay the processing fees"
                    />
                  </Grid>

                  {paymentType === "stripe" && (
                    <Elements
                      stripe={stripePromise}
                      options={{
                        mode: "payment",
                        amount: Math.round(parseFloat(finalPaymentAmount) * 100),
                        currency: "hkd",
                        paymentMethodCreation: "manual",
                        paymentMethodTypes: ["card", "link", "alipay"],
                      }}
                    >
                      <StripeCheckoutForm
                        amount={finalPaymentAmount}
                        teamId={selectedTeam.id}
                        teamName={selectedTeam.school}
                        transactionType={transactionType}
                        createStripeConfirmationQuote={
                          createStripeConfirmationQuote
                        }
                        finalizeStripePayment={finalizeStripePayment}
                        onProcessStripePayment={onProcessStripePayment}
                        onStripePaymentPending={onStripePaymentPending}
                        payProcessingFees={payProcessingFees}
                      />
                    </Elements>
                  )}

                  {paymentType === "paypal" && (
                    <PayPalScriptProvider
                      options={{
                        "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
                        components: "buttons",
                        currency: "HKD",
                      }}
                    >
                      <Grid item xs={12} md={6} style={{ marginBottom: "1rem" }}>
                        <FormControl component="fieldset">
                          <FormLabel component="legend">
                            PayPal account/card holder location
                          </FormLabel>
                          <RadioGroup
                            name="paypalAccountLocation"
                            value={paypalCardCountry}
                            onChange={(e) =>
                              setPaypalCardCountry(e.target.value)
                            }
                            row
                          >
                            <FormControlLabel
                              value="INTL"
                              control={<Radio color="primary" />}
                              label="Outside Hong Kong"
                            />
                            <FormControlLabel
                              value="HK"
                              control={<Radio color="primary" />}
                              label="Hong Kong"
                            />
                          </RadioGroup>
                          <Typography variant="caption">
                            Used only to estimate PayPal processing fees before
                            payment.
                          </Typography>
                        </FormControl>
                      </Grid>
                      {paypalFeeQuote && (
                        <Typography style={{ marginBottom: "1rem" }}>
                          <strong>
                            PayPal total:{" "}
                            {asHKCurrency(paypalFeeQuote.grossAmount)}
                          </strong>{" "}
                          {payProcessingFees
                            ? `(includes estimated ${asHKCurrency(paypalFeeQuote.feeAmount)} processing fee)`
                            : "(excludes processing fee)"}
                        </Typography>
                      )}
                      <PayPalButtons
                        forceReRender={[
                          finalPaymentAmount,
                          paypalFeeQuote?.grossAmount,
                        ]}
                        fundingSource={"paypal"}
                        onClick={validatePaymentAmount}
                        createOrder={(data, actions) =>
                          createOrder(data, actions, "paypal-wallet")
                        }
                        onApprove={(data, actions) => onApprove(data, actions)}
                      />
                      <PayPalButtons
                        forceReRender={[
                          finalPaymentAmount,
                          paypalFeeQuote?.grossAmount,
                        ]}
                        fundingSource={"card"}
                        onClick={validatePaymentAmount}
                        createOrder={(data, actions) =>
                          createOrder(data, actions, "paypal-card")
                        }
                        onApprove={(data, actions) => onApprove(data, actions)}
                      />
                    </PayPalScriptProvider>
                  )}
                </>
              )}
              {paymentType === "bankTransfer" && (
                <>
                  {showBankTransferRegInstructions && (
                    <Typography style={{ marginTop: "1rem" }}>
                      Your Application for Team Registration will only be deemed
                      complete and ready for review after valid proof of bank
                      transfer is received by the Vis East Moot Administration.
                    </Typography>
                  )}
                  {!showBankTransferRegInstructions && (
                    <Typography variant="h5" style={{ marginTop: "1rem" }}>
                      {paymentLabel}
                    </Typography>
                  )}
                  <Typography style={{ marginTop: "1rem" }}>
                    Please email your proof of bank transfer to{" "}
                    <a href="mailto:info@cisgmoot.org" className="color-blue">
                      info@cisgmoot.org
                    </a>
                    . Please ensure that your School is included in the
                    Reference/Message for easy traceability.
                  </Typography>
                  <Typography style={lineStyle}>
                    <span style={labelStyle}>Beneficiary Name:</span> Vis East
                    Moot Foundation Ltd.
                  </Typography>
                  <Typography style={lineStyle}>
                    <span style={labelStyle}>Bank:</span> HSBC, 1 Queen's Road
                    Central, Hong Kong
                  </Typography>
                  <Typography style={lineStyle}>
                    <span style={labelStyle}>Account Number:</span> 817 365901
                    001
                  </Typography>
                  <Typography style={lineStyle}>
                    <span style={labelStyle}>Swift Code:</span> HSBCHKHHHKH
                  </Typography>
                  <Typography style={lineStyle}>
                    <span style={labelStyle}>
                      For local Hong Kong transfers, Bank Code:
                    </span>{" "}
                    004
                  </Typography>
                  <Typography style={lineStyle}>
                    <span style={labelStyle}>Beneficiary Address:</span> Room
                    106, 1/Floor, Justice Place West Wing, 11 Ice House Street,
                    Central, Hong Kong
                  </Typography>
                  <Typography style={lineStyle}>
                    <span style={labelStyle}>Reference:</span> For{" "}
                    {existingTeamData.school}
                  </Typography>
                  <Typography
                    style={{ margin: "2.5rem 0", fontWeight: "bold" }}
                  >
                    Please note that payment must be made in either Hong Kong
                    Dollars or U.S. Dollars. Any and all bank charges must be
                    borne by the sender.
                  </Typography>
                </>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

export default connect(
  (state, ownProps) => ({
    router: state.router,
    existingTeamData: state.registration.teamData,
    selectedTeam: state.team.selectedTeam,
    onPaymentTypeChanged: ownProps.onPaymentTypeChanged,
    onPaymentCompleted: ownProps.onPaymentCompleted,
    paymentOverride: ownProps.paymentOverride,
    showExsitingPaymentNotification: ownProps.showExsitingPaymentNotification,
    transactionType: ownProps.transactionType,
    minAmount: ownProps.minAmount,
    maxAmount: ownProps.maxAmount,
    initialAmount: ownProps.initialAmount,
    paymentHelperText: ownProps.paymentHelperText,
    paymentLabel: ownProps.paymentLabel,
    showBankTransferRegInstructions: ownProps.showBankTransferRegInstructions,
    onPaymentInProcess: ownProps.onPaymentInProcess,
    balanceOwed: ownProps.balanceOwed,
  }),
  {
    saveTeamOrder: saveTeamOrder,
    getSelectedTeam: getSelectedTeam,
    createStripeConfirmationQuote: createStripeConfirmationQuote,
    finalizeStripePayment: finalizeStripePayment,
    createPaymentFeeQuote: createPaymentFeeQuote,
  },
)(PaymentProcessing);
